import * as React from 'react';
import { ElementLoader, PromptConfirmation } from 'ui';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { DuplicateRowsComponentProps } from 'app/area/indicator-factors/types';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { Location, Unit, Source, Equipment } from 'app/area/indicator-factors/list/duplicate-rows/code-pickers';
import { ValidFrom } from 'app/area/indicator-factors/list/duplicate-rows/code-fields';
import { DuplicateRowsValidationErrors } from 'app/area/indicator-factors/list/duplicate-rows/duplicate-rows-validation-errors';
import {
  allFieldsEmpty, getParametersId, isDisabled, hasFieldValueChanged, isCommentsValid
} from 'app/area/indicator-factors/list/duplicate-rows/duplicate-rows-helpers';
import { ModalSize } from 'app/components/constants';
import { Comments } from 'app/area/indicator-factors/list/duplicate-rows/code-fields/comments';
import styles from './duplicate-rows.scss';

interface DuplicateRowsComponentState {
  shouldDisplayModal: boolean;
}

export class DuplicateRowsModal extends React.PureComponent<DuplicateRowsComponentProps, DuplicateRowsComponentState> {

  constructor(props: DuplicateRowsComponentProps) {
    super(props);

    this.state = {
      shouldDisplayModal: undefined
    };
  }

  public componentDidUpdate(prevProps: DuplicateRowsComponentProps) {
    this.handleIsDirty(prevProps);
  }

  private handleIsDirty = (prevProps: DuplicateRowsComponentProps) => {
    hasFieldValueChanged(prevProps, this.props) && this.props.updateIsDirty(true);
  };

  private toggleModal = () => {
    this.setState({ shouldDisplayModal: !this.state.shouldDisplayModal });
  };

  private continue = () => {
    this.toggleModal();
    this.props.save();
    this.props.updateIsDirty(false);
  };

  private onClose = () => {
    this.props.onClose();
    this.props.updateIsDirty(false);
  };

  public render() {
    const { list: { numberOfResults, fetchingDuplicateRows,
      duplicateRows: { codeParameters, fetchingLoadCodeParameters, form } = {} }, metadata: { data: metadata },
      validation, locale, translation } = this.props;

    const fetching = fetchingDuplicateRows || fetchingLoadCodeParameters;
    const disabled = fetching || allFieldsEmpty(this.props) || isDisabled(this.props) || isCommentsValid(form);

    return (<>
      <ModalWrapper
        headerTitle={`${translation.duplicate_rows_description} (${numberOfResults})`}
        btnTitle={translation.duplicate_rows_description}
        btnCancelTitle={translation.cancel}
        onClose={this.onClose}
        onDone={this.toggleModal}
        closeOnDone={false}
        size={ModalSize.large}
        isCancelDisabled={fetching}
        isDisabled={disabled}
        canCloseOnBackground={!fetching}
        canCloseOnXButton={!fetching}>

        {fetching
          ? <ElementLoader />
          : <div>
            <p className={styles.multilineText}>
              {replaceTemplateString(
                translation.duplicate_rows_long_description,
                numberOfResults
              )}
            </p>

            <DuplicateRowsValidationErrors
              codeParameters={codeParameters}
              numberOfResults={numberOfResults}
              numberOfNotValidIndicators={getParametersId(this.props)?.length}
              validation={validation}
              locale={locale}
              translation={translation}
            />

            <div className="row mt-3">
              {metadata.location && <Location {...this.props} />}
              {metadata.unit && <Unit {...this.props} />}
              {metadata.source && <Source {...this.props} />}
              {metadata.equipment && <Equipment {...this.props} />}
            </div>

            <div className="row">
              <div className="col-md-6 col-sm-12">
                <ValidFrom {...this.props} />
              </div>
              <div className="col-md-6 col-sm-12">
                <Comments {...this.props} />
              </div>
            </div>
          </div>}
      </ModalWrapper>

      {this.state.shouldDisplayModal && <PromptConfirmation
        title={translation.duplicate_rows_description}
        description={replaceTemplateString(translation.confirm_duplicate_rows, numberOfResults)}
        continueText={translation.duplicate_rows_description}
        cancelText={translation.cancel}
        onContinue={this.continue}
        onCancel={this.toggleModal}
      />}
    </>);
  }
}
